import type { Segno } from '../types'
import { roundTenth } from './segmentQueue'

export type AutoSplitPreset = 'short' | 'normal' | 'long'

export interface MusicStructureFrame {
  time: number
  rms: number
  chroma: number[]
  bands: number[]
  onset: number
}

export interface BoundaryEvidence {
  time: number
  harmonic: number
  timbre: number
  rhythm: number
  repetition: number
  onset: number
  energyChange: number
  quiet: number
  score: number
}

interface AutoSplitProfile {
  minSeconds: number
  targetSeconds: number
  maxSeconds: number
}

const ANALYSIS_SAMPLE_RATE = 8192
const FFT_SIZE = 1024
const FRAME_HOP_SECONDS = 0.25
const CONTEXT_SECONDS = 3
const REPETITION_MIN_GAP_SECONDS = 20
const REPETITION_SAMPLE_SECONDS = 4

const AUTO_SPLIT_PROFILES: Record<AutoSplitPreset, AutoSplitProfile> = {
  short: { minSeconds: 6, targetSeconds: 10, maxSeconds: 18 },
  normal: { minSeconds: 10, targetSeconds: 20, maxSeconds: 32 },
  long: { minSeconds: 15, targetSeconds: 30, maxSeconds: 48 },
}

function clamp(value: number, minimum: number, maximum: number): number {
  return Math.min(maximum, Math.max(minimum, value))
}

function floorTenth(value: number): number {
  return Math.max(0, Math.floor((value + Number.EPSILON) * 10) / 10)
}

function percentile(values: number[], fraction: number): number {
  if (values.length === 0) return 0
  const sorted = [...values].sort((left, right) => left - right)
  const index = Math.round(clamp(fraction, 0, 1) * (sorted.length - 1))
  return sorted[index]
}

function normalizeVector(values: number[]): number[] {
  const norm = Math.sqrt(values.reduce((sum, value) => sum + value * value, 0))
  if (norm <= 1e-12) return values.map(() => 0)
  return values.map((value) => value / norm)
}

function cosineSimilarity(left: number[], right: number[]): number {
  const size = Math.min(left.length, right.length)
  if (size === 0) return 0
  let dot = 0
  let leftNorm = 0
  let rightNorm = 0
  for (let index = 0; index < size; index += 1) {
    dot += left[index] * right[index]
    leftNorm += left[index] * left[index]
    rightNorm += right[index] * right[index]
  }
  if (leftNorm <= 1e-12 || rightNorm <= 1e-12) return 0
  return clamp(dot / Math.sqrt(leftNorm * rightNorm), -1, 1)
}

function cosineDistance(left: number[], right: number[]): number {
  return clamp(1 - cosineSimilarity(left, right), 0, 1)
}

function fft(real: Float64Array, imaginary: Float64Array): void {
  const size = real.length

  for (let index = 1, reverse = 0; index < size; index += 1) {
    let bit = size >> 1
    for (; reverse & bit; bit >>= 1) reverse ^= bit
    reverse ^= bit
    if (index < reverse) {
      const realValue = real[index]
      real[index] = real[reverse]
      real[reverse] = realValue
      const imaginaryValue = imaginary[index]
      imaginary[index] = imaginary[reverse]
      imaginary[reverse] = imaginaryValue
    }
  }

  for (let length = 2; length <= size; length <<= 1) {
    const angle = -2 * Math.PI / length
    const rootReal = Math.cos(angle)
    const rootImaginary = Math.sin(angle)

    for (let start = 0; start < size; start += length) {
      let twiddleReal = 1
      let twiddleImaginary = 0
      const half = length >> 1

      for (let offset = 0; offset < half; offset += 1) {
        const even = start + offset
        const odd = even + half
        const oddReal = real[odd] * twiddleReal - imaginary[odd] * twiddleImaginary
        const oddImaginary = real[odd] * twiddleImaginary + imaginary[odd] * twiddleReal

        real[odd] = real[even] - oddReal
        imaginary[odd] = imaginary[even] - oddImaginary
        real[even] += oddReal
        imaginary[even] += oddImaginary

        const nextReal = twiddleReal * rootReal - twiddleImaginary * rootImaginary
        twiddleImaginary = twiddleReal * rootImaginary + twiddleImaginary * rootReal
        twiddleReal = nextReal
      }
    }
  }
}

function spectrumFeatures(real: Float64Array, imaginary: Float64Array): { chroma: number[]; bands: number[] } {
  const chroma = Array.from({ length: 12 }, () => 0)
  const bands = Array.from({ length: 8 }, () => 0)
  const nyquist = ANALYSIS_SAMPLE_RATE / 2

  for (let bin = 1; bin < FFT_SIZE / 2; bin += 1) {
    const frequency = bin * ANALYSIS_SAMPLE_RATE / FFT_SIZE
    if (frequency < 60 || frequency > nyquist) continue

    const power = real[bin] * real[bin] + imaginary[bin] * imaginary[bin]
    if (power <= 0) continue

    const bandPosition = Math.log(frequency / 60) / Math.log(nyquist / 60)
    const bandIndex = clamp(Math.floor(bandPosition * bands.length), 0, bands.length - 1)
    bands[bandIndex] += power

    if (frequency >= 80 && frequency <= 2000) {
      const midi = Math.round(69 + 12 * Math.log2(frequency / 440))
      const pitchClass = ((midi % 12) + 12) % 12
      chroma[pitchClass] += power
    }
  }

  return {
    chroma: normalizeVector(chroma),
    bands: normalizeVector(bands),
  }
}

/**
 * 브라우저 안에서만 음악 특징을 추출합니다.
 * 음량 자체가 아니라 화성(chroma), 음색(coarse spectrum), 리듬 변화(onset)를
 * 함께 사용하기 위한 구조 분석 프레임을 만듭니다.
 */
export async function extractMusicStructureFrames(audioBuffer: AudioBuffer): Promise<MusicStructureFrame[]> {
  const sourceRate = audioBuffer.sampleRate
  const channels = Array.from(
    { length: Math.max(1, audioBuffer.numberOfChannels) },
    (_, index) => audioBuffer.getChannelData(index),
  )
  const sourceStep = sourceRate / ANALYSIS_SAMPLE_RATE
  const frames: MusicStructureFrame[] = []
  let previousBands: number[] | null = null
  let previousRms = 0

  for (let time = 0; time < audioBuffer.duration; time += FRAME_HOP_SECONDS) {
    const real = new Float64Array(FFT_SIZE)
    const imaginary = new Float64Array(FFT_SIZE)
    const sourceStart = time * sourceRate
    let rmsSum = 0
    let rmsCount = 0

    for (let index = 0; index < FFT_SIZE; index += 1) {
      const sourceIndex = Math.floor(sourceStart + index * sourceStep)
      let mono = 0
      if (sourceIndex < audioBuffer.length) {
        for (const channel of channels) mono += channel[sourceIndex] ?? 0
        mono /= channels.length
      }

      rmsSum += mono * mono
      rmsCount += 1
      const window = 0.5 - 0.5 * Math.cos(2 * Math.PI * index / (FFT_SIZE - 1))
      real[index] = mono * window
    }

    const rms = rmsCount > 0 ? Math.sqrt(rmsSum / rmsCount) : 0
    fft(real, imaginary)
    const features = spectrumFeatures(real, imaginary)

    let onset = Math.max(0, rms - previousRms) * 2
    if (previousBands) {
      for (let index = 0; index < features.bands.length; index += 1) {
        onset += Math.max(0, features.bands[index] - previousBands[index])
      }
    }

    frames.push({
      time,
      rms,
      chroma: features.chroma,
      bands: features.bands,
      onset,
    })
    previousBands = features.bands
    previousRms = rms

    // 긴 음원에서도 브라우저 UI가 완전히 멈추지 않도록 주기적으로 이벤트 루프를 양보합니다.
    if (frames.length % 64 === 0) {
      await new Promise<void>((resolve) => window.setTimeout(resolve, 0))
    }
  }

  return frames
}

function averageRange(
  frames: MusicStructureFrame[],
  start: number,
  end: number,
): { chroma: number[]; bands: number[]; rms: number; onset: number } {
  const chroma = Array.from({ length: 12 }, () => 0)
  const bands = Array.from({ length: 8 }, () => 0)
  let rms = 0
  let onset = 0
  const count = Math.max(1, end - start)

  for (let frameIndex = start; frameIndex < end; frameIndex += 1) {
    const frame = frames[frameIndex]
    rms += frame.rms
    onset += frame.onset
    for (let index = 0; index < chroma.length; index += 1) chroma[index] += frame.chroma[index] ?? 0
    for (let index = 0; index < bands.length; index += 1) bands[index] += frame.bands[index] ?? 0
  }

  return {
    chroma: normalizeVector(chroma.map((value) => value / count)),
    bands: normalizeVector(bands.map((value) => value / count)),
    rms: rms / count,
    onset: onset / count,
  }
}

function contextFingerprint(context: ReturnType<typeof averageRange>): number[] {
  return normalizeVector([
    ...context.chroma,
    ...context.bands,
    context.onset,
  ])
}

/**
 * 경계 양쪽의 몇 초를 비교해서 '음악적 문맥이 바뀌는가'를 점수화합니다.
 * RMS/quiet는 보조 점수(5%)만 차지하고, 화성·음색·리듬·반복 문맥을 우선합니다.
 */
export function buildBoundaryEvidence(frames: MusicStructureFrame[]): BoundaryEvidence[] {
  if (frames.length === 0) return []

  const hop = frames.length > 1
    ? Math.max(0.05, frames[1].time - frames[0].time)
    : FRAME_HOP_SECONDS
  const contextFrames = Math.max(2, Math.round(CONTEXT_SECONDS / hop))
  const repetitionGapFrames = Math.max(contextFrames * 2, Math.round(REPETITION_MIN_GAP_SECONDS / hop))
  const repetitionStride = Math.max(1, Math.round(REPETITION_SAMPLE_SECONDS / hop))
  const rmsHigh = Math.max(percentile(frames.map((frame) => frame.rms), 0.85), 1e-6)
  const onsetHigh = Math.max(percentile(frames.map((frame) => frame.onset), 0.90), 1e-6)

  const rightContexts: Array<ReturnType<typeof averageRange> | null> = frames.map((_, index) => {
    if (index + contextFrames > frames.length) return null
    return averageRange(frames, index, index + contextFrames)
  })
  const rightFingerprints = rightContexts.map((context) => context ? contextFingerprint(context) : null)

  return frames.map((frame, index) => {
    if (index < contextFrames || index + contextFrames >= frames.length) {
      return {
        time: frame.time,
        harmonic: 0,
        timbre: 0,
        rhythm: 0,
        repetition: 0,
        onset: 0,
        energyChange: 0,
        quiet: 0,
        score: 0,
      }
    }

    const left = averageRange(frames, index - contextFrames, index)
    const right = rightContexts[index] ?? averageRange(frames, index, index + contextFrames)
    const harmonic = cosineDistance(left.chroma, right.chroma)
    const timbre = cosineDistance(left.bands, right.bands)
    const rhythm = clamp(Math.abs(left.onset - right.onset) / Math.max(0.05, left.onset + right.onset), 0, 1)
    const energyChange = clamp(Math.abs(Math.log((left.rms + 1e-6) / (right.rms + 1e-6))) / 3, 0, 1)
    const quiet = clamp(1 - frame.rms / rmsHigh, 0, 1)

    let localOnset = frame.onset
    if (index > 0) localOnset = Math.max(localOnset, frames[index - 1].onset)
    if (index + 1 < frames.length) localOnset = Math.max(localOnset, frames[index + 1].onset)
    const onset = clamp(localOnset / onsetHigh, 0, 1)

    let repetition = 0
    const fingerprint = rightFingerprints[index]
    if (fingerprint) {
      const latestPrior = index - repetitionGapFrames
      for (let prior = contextFrames; prior <= latestPrior; prior += repetitionStride) {
        const priorFingerprint = rightFingerprints[prior]
        if (!priorFingerprint) continue
        const similarity = cosineSimilarity(fingerprint, priorFingerprint)
        repetition = Math.max(repetition, clamp((similarity - 0.75) / 0.25, 0, 1))
      }
    }

    const score = clamp(
      harmonic * 0.34
      + timbre * 0.24
      + rhythm * 0.14
      + repetition * 0.10
      + onset * 0.08
      + energyChange * 0.05
      + quiet * 0.05,
      0,
      1,
    )

    return {
      time: frame.time,
      harmonic,
      timbre,
      rhythm,
      repetition,
      onset,
      energyChange,
      quiet,
      score,
    }
  })
}

function makeSegnos(boundaries: number[]): Segno[] {
  return boundaries.slice(0, -1).map((startTime, index) => ({
    order: index + 1,
    title: `Segno ${String(index + 1).padStart(2, '0')}`,
    startTime,
    endTime: boundaries[index + 1],
    lyrics: '',
  }))
}

/**
 * 목표 길이는 '선호'일 뿐입니다. 실제 선택 점수의 88%는 음악 구조 경계이고
 * 길이 선호는 12%만 반영하여 기계적인 고정 간격 분할보다 문맥 변화점을 우선합니다.
 */
export function automaticSegnosFromBoundaryEvidence(
  evidence: BoundaryEvidence[],
  durationSeconds: number,
  preset: AutoSplitPreset,
): Segno[] {
  const safeEnd = floorTenth(durationSeconds)
  if (!Number.isFinite(safeEnd) || safeEnd < 0.1) return []

  const profile = AUTO_SPLIT_PROFILES[preset]
  if (safeEnd <= profile.maxSeconds) return makeSegnos([0, safeEnd])

  const boundaries = [0]
  let current = 0

  while (safeEnd - current > profile.maxSeconds) {
    const minimumTime = current + profile.minSeconds
    const maximumTime = Math.min(current + profile.maxSeconds, safeEnd - profile.minSeconds)
    if (maximumTime <= minimumTime) break

    const idealTime = Math.min(current + profile.targetSeconds, maximumTime)
    const candidates = evidence.filter((item) => item.time >= minimumTime && item.time <= maximumTime)
    if (candidates.length === 0) break

    let best = candidates[0]
    let bestCombined = Number.NEGATIVE_INFINITY
    const lengthScale = Math.max(1, profile.maxSeconds - profile.minSeconds)

    for (let index = 0; index < candidates.length; index += 1) {
      const item = candidates[index]
      const lengthPreference = 1 - clamp(Math.abs(item.time - idealTime) / lengthScale, 0, 1)
      const previousScore = index > 0 ? candidates[index - 1].score : item.score
      const nextScore = index + 1 < candidates.length ? candidates[index + 1].score : item.score
      const prominence = clamp(item.score - (previousScore + nextScore) / 2, 0, 1)
      const combined = item.score * 0.86 + lengthPreference * 0.12 + prominence * 0.02

      if (combined > bestCombined) {
        bestCombined = combined
        best = item
      }
    }

    const cut = roundTenth(best.time)
    if (cut <= current + 0.05 || safeEnd - cut < profile.minSeconds - 0.05) break
    boundaries.push(cut)
    current = cut
  }

  boundaries.push(safeEnd)
  return makeSegnos(boundaries)
}

export async function automaticSegnosFromMusicStructure(
  audioBuffer: AudioBuffer,
  preset: AutoSplitPreset,
): Promise<Segno[]> {
  const frames = await extractMusicStructureFrames(audioBuffer)
  return automaticSegnosFromBoundaryEvidence(
    buildBoundaryEvidence(frames),
    audioBuffer.duration,
    preset,
  )
}
